import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import Seo, { breadcrumbJsonLd } from '../components/Seo.jsx';
import ContentRenderer, { extractHeadings } from '../components/blog/ContentRenderer.jsx';
import { PostHeader, PostHeroImage, PostFaqs, PostTags, PostCta, PostSidebar, RelatedPosts } from '../components/blog/PostParts.jsx';
import { blogApi, imageUrl, readingTime, PATHS } from '../lib/opinly';
import { track } from '../lib/analytics';

const NAVY = '#1a3a5c';
const SKELETON_LINES = ['l1', 'l2', 'l3', 'l4', 'l5', 'l6'];

function NotFound() {
  return (
    <div data-testid="post-not-found" className="max-w-xl mx-auto my-24 px-5 text-center">
      <span className="font-semibold uppercase tracking-wider text-xs" style={{ color: '#3d94b8' }}>404</span>
      <h1 className="mt-3 text-3xl font-extrabold" style={{ color: NAVY }}>Beitrag nicht gefunden</h1>
      <p className="mt-3 text-slate-600">Dieser Artikel existiert nicht mehr oder wurde verschoben.</p>
      <Link to={PATHS.index} className="inline-block mt-6"><Button className="btn-teal rounded-full h-11 px-6"><ArrowLeft className="mr-2 h-4 w-4" /> Zum Blog</Button></Link>
    </div>
  );
}

function Skeleton() {
  return (
    <div data-testid="post-loading" className="max-w-3xl mx-auto px-5 py-16 animate-pulse space-y-4" aria-busy="true">
      <div className="h-3 w-32 bg-slate-100 rounded" /><div className="h-10 w-full bg-slate-100 rounded" /><div className="h-10 w-2/3 bg-slate-100 rounded" />
      <div className="aspect-[16/9] bg-slate-100 rounded-2xl mt-8" />
      {SKELETON_LINES.map((k, i) => <div key={k} className="h-4 bg-slate-100 rounded" style={{ width: `${90 - (i % 3) * 15}%` }} />)}
    </div>
  );
}

function LoadError() {
  return (
    <div role="alert" data-testid="post-error" className="max-w-xl mx-auto my-24 px-5 text-center">
      <h1 className="text-2xl font-extrabold" style={{ color: NAVY }}>Beitrag konnte nicht geladen werden</h1>
      <Button onClick={() => window.location.reload()} variant="outline" className="btn-outline-teal rounded-full mt-6 h-11 px-6">Erneut versuchen</Button>
    </div>
  );
}

function usePost(slug) {
  const [state, setState] = useState({ post: null, status: 'loading', related: [] });
  useEffect(() => {
    let active = true;
    setState({ post: null, status: 'loading', related: [] });
    window.scrollTo({ top: 0 });
    (async () => {
      try {
        const post = await blogApi.post(slug);
        if (!active) return;
        setState({ post, status: 'ok', related: [] });
        track('blog_post_view', { slug, title: post.title, category: post.category?.slug });
        try {
          const r = await blogApi.posts({ limit: 4, category: post.category?.slug });
          if (active) setState((s) => ({ ...s, related: r.data.filter((x) => x.slug !== slug).slice(0, 3) }));
        } catch (err) {
          console.warn('[blog] related posts unavailable', err);
        }
      } catch (err) {
        if (active) setState({ post: null, status: err.status === 404 ? 'notfound' : 'error', related: [] });
      }
    })();
    return () => { active = false; };
  }, [slug]);
  return state;
}

function buildJsonLd(post, url, hero) {
  return [
    {
      '@context': 'https://schema.org', '@type': 'BlogPosting',
      headline: post.title, description: post.metaDescription || post.description,
      image: hero ? [hero] : undefined, datePublished: post.firstPublishedAt, dateModified: post.modifiedAt,
      author: post.author ? { '@type': 'Person', name: post.author.name, url: `${window.location.origin}${PATHS.author(post.author.slug)}` } : undefined,
      publisher: { '@type': 'Organization', name: 'AvantiTutor', url: window.location.origin },
      mainEntityOfPage: { '@type': 'WebPage', '@id': url }, url,
      articleSection: post.category?.name, keywords: (post.tags || []).map((t) => t.name).join(', ') || undefined, inLanguage: 'de-DE',
    },
    breadcrumbJsonLd([{ name: 'Startseite', path: '/' }, { name: 'Blog', path: PATHS.index }, ...(post.category ? [{ name: post.category.name, path: PATHS.category(post.category.slug) }] : []), { name: post.title, path: PATHS.post(post.slug) }]),
    post.faqs?.length ? { '@context': 'https://schema.org', '@type': 'FAQPage', mainEntity: post.faqs.map((f) => ({ '@type': 'Question', name: f.question, acceptedAnswer: { '@type': 'Answer', text: f.answer } })) } : null,
  ];
}

export default function BlogPost() {
  const { slug } = useParams();
  const { post, status, related } = usePost(slug);
  const [copied, setCopied] = useState(false);

  if (status === 'loading') return <Skeleton />;
  if (status === 'notfound') return <><Seo title="Beitrag nicht gefunden" noindex /><NotFound /></>;
  if (status === 'error') return <LoadError />;

  const hero = imageUrl(post.titleFile?.fileKey);
  const url = `${window.location.origin}${PATHS.post(post.slug)}`;

  const share = async () => {
    track('share', { method: navigator.share ? 'native' : 'copy', slug: post.slug });
    try {
      if (navigator.share) { await navigator.share({ title: post.title, url }); return; }
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      if (err?.name !== 'AbortError') console.warn('[blog] share failed', err);
    }
  };

  return (
    <article>
      <Seo title={post.metaTitle || post.title} description={post.metaDescription || post.description} image={hero} type="article" path={PATHS.post(post.slug)} jsonLd={buildJsonLd(post, url, hero)} />
      <PostHeader post={post} minutes={readingTime(post.content)} onShare={share} copied={copied} />
      <PostHeroImage titleFile={post.titleFile} fallbackAlt={post.title} />
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-12 md:py-16 grid lg:grid-cols-[1fr_280px] gap-12">
        <div className="min-w-0 max-w-3xl">
          <ContentRenderer content={post.content} />
          <PostFaqs faqs={post.faqs} />
          <PostTags tags={post.tags} />
          <PostCta />
        </div>
        <PostSidebar headings={extractHeadings(post.content)} author={post.author} />
      </div>
      <RelatedPosts posts={related} />
    </article>
  );
}
